import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { BaseCalendar } from "src/components/DatePicker/BaseCalendar";
import {
  isDateRange,
  isDateRangeWithDates,
} from "src/components/DatePicker/utils/datePredicates";
import type { ComponentProps } from "react";
import { asDay } from "../../../utils/dateTime";
import { getTimeInTimeZone } from "./DateHelpers";

type CalendarValue = Date | [Date | null, Date | null] | null | undefined;

type Props = {
  isReturn: boolean;

  // Dates are as at midnight local time
  departureDate?: Date;
  returnDate?: Date;
  onDatesChange(departureDate: Date, returnDate?: Date): void;
  minDate?: Date;

  maxDaysAhead?: number; // TODO: Source this internally
  className?: string; // TODO: Not allow - use a wrapper instead (for now)
  timeZone?: string | number; // TODO: Source this internally
  showDoubleView?: boolean;
  value?: CalendarValue;
  isProjectingTripNights?: boolean;
  onClickDay?: ComponentProps<typeof BaseCalendar>["onClickDay"];
};

export function SearchDatesPicker({
  isReturn,
  departureDate,
  returnDate,
  onDatesChange,
  maxDaysAhead,
  className,
  timeZone,
  showDoubleView = false,
  value,
  isProjectingTripNights,
  onClickDay,
  minDate,
}: Props) {
  const now = getTimeInTimeZone(timeZone);
  let maxDate;
  if (maxDaysAhead) {
    maxDate = new Date(now);
    maxDate.setDate(now.getDate() + maxDaysAhead);
  }

  const fallbackValue: CalendarValue = departureDate
    ? returnDate && isReturn
      ? [departureDate, returnDate]
      : departureDate
    : null;

  return (
    <BaseCalendar
      onClickDay={onClickDay}
      selectRange={isReturn}
      allowPartialRange={isReturn}
      minDate={minDate ?? now}
      // Passing undefined results in the calendar not clearing the selection, so we pass null instead.
      maxDate={maxDate}
      value={value ?? fallbackValue}
      isProjectingTripNights={isProjectingTripNights}
      onChange={(value) => {
        // Note: the asDay's used are because dates returned from react-calendar are in UTC midnight, not local midnight.
        // We will eventually want to just use UTC everywhere for convenience, but for now we'll keep it as local midnight
        if (isDateRange(value)) {
          if (value[0]) {
            onDatesChange(
              asDay(value[0]),
              isReturn && isDateRangeWithDates(value)
                ? asDay(value[1])
                : undefined,
            );
          }
        } else if (value) {
          onDatesChange(asDay(value));
        }
      }}
      onActiveStartDateChange={() => {
        // See BaseCalendar.tsx - this will only be called when next/previous month is selected.
        sendAnalyticsInteractionEvent({
          category: "MultiFunctionDatePicker",
          action: "Scroll:Month",
        });
      }}
      showDoubleView={showDoubleView}
      className={className}
    />
  );
}
